import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addSimpleStaff } from '../../../redux/staffRelated/staffHandle';
import { useToast } from '../../../context/ToastContext';
import { underControl } from '../../../redux/staffRelated/staffSlice';
import { 
    CircularProgress, Box, Typography, TextField, MenuItem, Select, 
    FormControl, InputLabel, Button, Paper, Container, IconButton, Avatar, Divider, Grid
} from '@mui/material';
import { 
    CloudUpload as CloudUploadIcon, 
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    Phone as PhoneIcon,
    LocationOn as LocationOnIcon,
    CheckCircle as CheckCircleIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';

const AddStaff = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { status, response, error } = useSelector(state => state.staff);
    const { currentUser } = useSelector(state => state.user);

    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [position, setPosition] = useState('');
    const [phone, setPhone] = useState('');
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    const { showToast } = useToast();
    const [loader, setLoader] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const positions = [
        'Administration', 'Accountant', 'Librarian', 'Lab Assistant', 
        'Clerk', 'Peon', 'Security', 'Driver', 'Canteen Staff', 'Other'
    ];

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
    };

    const submitHandler = (event) => {
        event.preventDefault();
        setLoader(true);
        setIsSubmitting(true);
        const fields = photo ? { name, address, position, phone, photo } : { name, address, position, phone };
        dispatch(addSimpleStaff(fields, currentUser._id));
    };

    useEffect(() => {
        dispatch(underControl());
    }, [dispatch]);

    useEffect(() => {
        if (status === 'added' && isSubmitting) {
            setIsSubmitting(false);
            dispatch(underControl());
            showToast("Staff member added successfully!", "success");
            navigate("/Admin/staff");
        } else if (status === 'failed') {
            showToast(response || "Failed to add staff", "error");
            setLoader(false);
            setIsSubmitting(false);
        }
    }, [status, navigate, response, dispatch, isSubmitting]);

    return (
        <PageContainer>
            <StickyHeader elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2 }}>
                    <IconButton 
                        onClick={() => navigate("/Admin/staff")}
                        sx={{ color: 'white', mr: 2, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Register Staff</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Add a new non-teaching staff member</Typography>
                    </Box>
                </Box>
            </StickyHeader>

            <MainContent maxWidth="md">
                <Box component="form" onSubmit={submitHandler} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <StyledFormCard elevation={2}>
                                <StyledFormHeader color="#667eea">
                                    <Typography variant="h6">Personal Details</Typography>
                                    <PersonIcon />
                                </StyledFormHeader>
                                <StyledFormContent>
                                    <TextField fullWidth label="Full Name *" value={name} onChange={e => setName(e.target.value)} required sx={{ mb: 3 }} />
                                    <FormControl fullWidth required sx={{ mb: 3 }}>
                                        <InputLabel>Position</InputLabel>
                                        <Select value={position} label="Position" onChange={e => setPosition(e.target.value)}>
                                            {positions.map(pos => <MenuItem key={pos} value={pos}>{pos}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField fullWidth label="Phone Number" value={phone} onChange={e => setPhone(e.target.value)} sx={{ mb: 3 }} />
                                    <TextField fullWidth label="Address" multiline rows={2} value={address} onChange={e => setAddress(e.target.value)} />
                                </StyledFormContent>
                            </StyledFormCard>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <StyledFormCard elevation={2}>
                                <StyledFormHeader color="#a78bfa">
                                    <Typography variant="h6">Profile Photo</Typography>
                                    <CloudUploadIcon />
                                </StyledFormHeader>
                                <StyledFormContent>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                        {!photoPreview ? (
                                            <UploadZone onClick={() => document.getElementById('staff-photo').click()}>
                                                <CloudUploadIcon sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>Click to Upload</Typography>
                                                <Typography variant="caption" color="textSecondary">PNG, JPG up to 5MB</Typography>
                                            </UploadZone>
                                        ) : (
                                            <Box sx={{ position: 'relative' }}>
                                                <PreviewAvatar src={photoPreview} />
                                                <IconButton 
                                                    onClick={removePhoto}
                                                    sx={{ position: 'absolute', top: 0, right: 0, bgcolor: '#f44336', color: 'white', '&:hover': { bgcolor: '#d32f2f' } }}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                        <input id="staff-photo" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                                    </Box>
                                </StyledFormContent>
                            </StyledFormCard>
                            
                            <Box sx={{ mt: 4, textAlign: 'center' }}>
                                <SubmitButton
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={loader}
                                    startIcon={loader ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                >
                                    {loader ? 'Adding...' : 'Add Staff Member'}
                                </SubmitButton>
                            </Box>
                        </Grid>
                    </Grid>

                </Box>
            </MainContent>
        </PageContainer>
    );
};

export default AddStaff;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const PageContainer = styled.div`background-color: #f8fafc; min-height: 100vh;`;
const StickyHeader = styled(Paper)`
    position: sticky; top: 0; z-index: 1000; border-radius: 0 !important;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important; color: white !important;
`;
const MainContent = styled(Container)`padding-top: 40px; animation: ${fadeIn} 0.5s ease-out;`;
const StyledFormCard = styled(Paper)`border-radius: 24px !important; overflow: hidden; border: 1px solid #edf2f7; height: 100%;`;
const StyledFormHeader = styled(Box)`
    padding: 16px 24px; background: ${props => props.color || '#667eea'}; color: white;
    display: flex; justify-content: space-between; align-items: center;
`;
const StyledFormContent = styled(Box)`padding: 24px;`;
const UploadZone = styled(Box)`
    width: 100%; max-width: 300px; height: 160px; border: 2px dashed #667eea; border-radius: 20px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.3s ease; background: #f0f4ff;
    &:hover { background: #eef2ff; border-color: #764ba2; }
`;
const PreviewAvatar = styled(Avatar)`
    width: 140px !important; height: 140px !important; border-radius: 20px !important;
    border: 4px solid #667eea; box-shadow: 0 8px 25px rgba(0,0,0,0.1);
`;
const SubmitButton = styled(Button)`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; color: white !important;
    padding: 14px 40px !important; border-radius: 16px !important; font-weight: 700 !important;
    text-transform: none !important; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
`;
